<template>
  <div
    :key="threshold.publicId"
    :class="['cf-card cf-card--clickable cf-grid cf-grid--2-grid-column-left-large cf-flex--gap-16 cf-cursor-pointer cf-mobile-column', { 'cf-card--disabled': !isUnlockedThreshold }]"
    role="button"
    tabindex="0"
    @keyup.enter="offerDetailsClickHandler"
    @click="offerDetailsClickHandler"
  >
    <div
      v-if="thresholdOfferImage"
      class="cf-card--banners"
    >
      <div
        class="cf-card--banner"
        :class="[isUnlockedThreshold ? 'cf-card--background-brand-primary' : 'cf-card--background-gray-darker']"
      >
        {{ threshold.cost }}
        {{ loyalty.currency.pluralNameCapitalCase }}
      </div>
    </div>
    <div class="cf-flex cf-flex--row cf-flex--grow-2 cf-flex--gap-12">
      <img
        v-if="thresholdOfferImage"
        class="cf-image cf-image--thumbnail"
        :src="thresholdOfferImage"
        onerror="this.src='/images/image-not-found.png';"
      >
      <cf-offer-point-display
        v-else
        :cost="threshold.cost"
        :currency="loyalty.currency.pluralShortNameUpperCase"
        :is-disabled="!isUnlockedThreshold"
      />

      <div class="cf-flex cf-flex--column cf-flex--align-start cf-flex--justify-center cf-flex--gap-8">
        <v-clamp
          v-if="offer.title"
          tag="p"
          class="cf-title cf-title--4"
          autoresize
          :max-lines="hasOfferDescription ? 2 : 3"
        >
          {{ offer.title }}
        </v-clamp>

        <v-clamp
          v-if="hasOfferDescription"
          tag="p"
          class="cf-subtitle cf-subtitle--3"
          autoresize
          :max-lines="2"
        >
          {{ offer.description }}
        </v-clamp>
      </div>
    </div>

    <div class="cf-flex cf-flex--row cf-flex--grow-1 cf-flex--align-center cf-flex--justify-end">
      <button
        v-if="isUnlockedThreshold"
        :class="['cf-button cf-button--primary cf-button--md cf-button--mobile-fill cf-align-self-center', { 'cf-button--disabled': isDisabled }]"
        type="button"
        :disabled="isDisabled"
        @click.stop="redeemOfferClickHandler"
      >
        {{ redeemCtaContent }}
      </button>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import CfOfferPointDisplay from '@/view/components/globals/cf-offer-point-display/index.vue';


  export default {
    name: 'CfOfferThresholdCard',

    components: {
      CfOfferPointDisplay
    },

    props: {
      threshold: {
        type: Object,
        required: true
      },

      isOfferDisabled: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        previousRoute: ''
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('offers', ['loyalty']),
      ...mapGetters('merchant', ['merchant', 'unlockedRewardAnytimeConversionFlow', 'unlockedRewardInStoreRedemptionFlow']),
      ...mapGetters('session', ['currentUser']),

      isUnlockedThreshold() {
        return this.threshold.isManuallyRunnable;
      },

      isDisabled() {
        // Use new configuration path first
        const startOrderFromOfferEnabled = this.merchant.options?.offersOptions?.startOrderFromOfferEnabled !== undefined
          ? this.merchant.options.offersOptions.startOrderFromOfferEnabled
          : this.merchant.options.loyaltyOptions?.inStoreLoyaltyOptions?.startOrderFromOffer;

        const disableInStoreRedemption = this.merchant.options.loyaltyOptions?.inStoreLoyaltyOptions?.disableInStoreRedemption;

        const anytimePointConversionDisabled = !startOrderFromOfferEnabled;

        const isInStoreRedemptionFlowDisabled = disableInStoreRedemption || !this.currentUser.allowInStoreOfferRedemption;

        /** this.isOfferDisabled acts as an override */
        return this.isOfferDisabled || (anytimePointConversionDisabled && isInStoreRedemptionFlowDisabled);
      },

      offer() {
        return this.threshold.rewardOffer;
      },

      scanAndPayDisabled() {
        return this.merchant.features['disable-scan-and-pay'];
      },

      hasOfferDescription() {
        return this.offer.description?.trim().length;
      },

      thresholdOfferImage() {
        return this.threshold.rewardOffer.couponImageUrl;
      },

      redeemCtaContent() {
        const isFlowEnabled = flow => !!flow && flow !== 'None';

        let ctaKey;

        if (isFlowEnabled(this.unlockedRewardInStoreRedemptionFlow)) {
          ctaKey = 'unlockedRewardInStoreRedemptionCta';
        }

        else if (isFlowEnabled(this.unlockedRewardAnytimeConversionFlow)) {
          ctaKey = 'unlockedRewardAnytimeConversionCta';
        }

        return this.content?.Loyalty?.metadata?.[ctaKey] || 'Redeem';
      }
    },

    mounted() {
      this.mountedHandler();
    },

    methods: {
      mountedHandler() {
        this.previousRoute = this.$route.query?.origin;
      },

      offerDetailsClickHandler() {
        const modalPayload = { offer: this.threshold.rewardOffer, isLockedOffer: !this.isUnlockedThreshold };

        this.$openModal('offerDetails', modalPayload);
      },

      redeemOfferClickHandler() {
        const confirmationContent = this.content.LoyaltyRedeemConfirmationModal;

        const modalPayload = {
          title: confirmationContent.title,
          primaryText: confirmationContent.metadata.message,
          ctaConfirmText: confirmationContent.metadata.redeemCta,
          ctaCancelText: confirmationContent.metadata.cancelCta,
          onConfirm: this.convertPointsToOffer
        };

        this.$openModal('confirmation', modalPayload);
      },

      async convertPointsToOffer() {
        // Use new configuration path first
        const startOrderFromOfferEnabled = this.merchant.options?.offersOptions?.startOrderFromOfferEnabled !== undefined
          ? this.merchant.options.offersOptions.startOrderFromOfferEnabled
          : this.merchant.options.loyaltyOptions?.inStoreLoyaltyOptions?.startOrderFromOffer;

        if (this.unlockedRewardInStoreRedemptionFlow === 'ActivateOfferKeyForSingleScan') {
          // New flow: Use offerKeyToken directly, skip eager point conversion
          // Backend will handle point conversion when the order redeems

          let routeName = this.previousRoute;
          if (!routeName) {
            routeName = this.scanAndPayDisabled ? 'scanOnly' : 'scanToPay';
          }

          this.$router.push({
            name: routeName,
            query: {
              thresholdPublicId: this.threshold.publicId
            }
          });
        }
        else if (this.unlockedRewardInStoreRedemptionFlow === 'GenerateCheckinTokenForDisplay') {
          // Legacy Torchy's flow: Generate pending redemption directly
          const pendingOffer = await this.$store.dispatch('offers/convertPendingPoints', this.threshold.publicId);

          if (pendingOffer) {
            this.$openModal('offerInStoreRedemption', { offer: pendingOffer });
          }
        }
        else if ((this.unlockedRewardInStoreRedemptionFlow === 'SeparateConvertAndGenerateCheckinTokenForDisplay' || this.unlockedRewardInStoreRedemptionFlow === 'SeparateConvertAndGenerateRedemptionCodeForSingleScan')) {
          // Legacy flows: Convert points first, then handle redemption
          await this.$store.dispatch('offers/convertPointsByThreshold', this.threshold);
        }
        // If flow is 'None' or unsupported, do nothing (redemption is disabled)

        if (startOrderFromOfferEnabled) {
          this.$emit('set-selected-tab');
        }
      }
    }
  };
</script>
